import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  title: string;
  icon?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
}

function getPortalRoot(): HTMLElement {
  let el = document.getElementById('sl-portal-root');
  if (!el) {
    // Fallback if the root div is missing for some reason.
    el = document.createElement('div');
    el.id = 'sl-portal-root';
    document.body.appendChild(el);
  }
  return el;
}

export function Modal({ title, icon, onClose, children, footer, maxWidth }: Props): React.ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while a modal is open to avoid awkward overlay scroll chaining.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const content = (
    <div className="sl-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="sl-modal"
        style={maxWidth ? { maxWidth } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sl-modal-header">
          <h2>
            {icon ? <span style={{ marginRight: 8 }}>{icon}</span> : null}
            {title}
          </h2>
          <button type="button" className="sl-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="sl-modal-body">{children}</div>
        {footer && <div className="sl-modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(content, getPortalRoot());
}
